import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const { login, currentUser, dbUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Redirect to home page if user is already authenticated
  useEffect(() => {
    if (currentUser && dbUser) {
      navigate('/', { replace: true });
    }
  }, [currentUser, dbUser, navigate]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await login();
      // Navigation will be handled by the useEffect hook above
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom color="primary">
                🏥 Pharmacy
              </Typography>
              <Typography variant="h5" component="h2" gutterBottom>
                Order Intake System
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sign in with your authorized Gmail account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleLogin}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <GoogleIcon />}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </Button>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
                🔒 This application is restricted to authorized users only.
                <br />
                Please contact your administrator if you need access.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
